import { Effect, Match, Ref } from "effect";
import { chatResourceKey, type ChatAction, type ChatSubmission } from "./model.ts";
import type { ChatSession } from "./service.ts";
import type { makeChatController } from "./operations.ts";
type Controller = Effect.Success<ReturnType<typeof makeChatController>>;
export const makeChatActions = (controller: Controller): ChatSession => {
  const { state, read, snapshot, receipts, receive, select, kick, create, respond, admit, release, update, activeStation, invalidate, finishReceipt, accepted, api } = controller;
  const send = (submission: ChatSubmission): Effect.Effect<boolean> => accepted.join(Effect.gen(function* () {
    const { stationId, threadId } = submission, text = submission.text.trim(), key = chatResourceKey("send", threadId);
    const before = yield* Ref.get(state);
    if (!text || !threadId || before.view.config?.configured === false || !(yield* admit(stationId, key))) return false;
    yield* update(current => ({ ...current, view: { ...current.view, sendingThreads: [...current.view.sendingThreads, threadId] } }));
    const reply = yield* respond(api.message(stationId, threadId, { text, ...(submission.attachments.length ? { attachments: submission.attachments } : {}) }));
    yield* release(key);
    if (activeStation(yield* Ref.get(state), stationId)) {
      yield* update(current => {
        const drafts = new Map(current.drafts);
        if (reply._tag === "Success") drafts.delete(threadId);
        return { ...current, drafts, view: { ...current.view,
          sendingThreads: current.view.sendingThreads.filter(id => id !== threadId),
          ...(reply._tag === "Failure" ? { threadError: { threadId, message: reply.message } } : {}),
          ...(reply._tag === "Success" && current.view.threadId === threadId ? { draft: "", turnActive: true } : {}) } };
      });
      if (reply._tag === "Success" && (yield* Ref.get(state)).view.threadId === threadId) yield* kick;
    }
    yield* finishReceipt("send", stationId, threadId, null, reply);
    return reply._tag === "Success";
  }));
  const dispatch = (action: ChatAction): Effect.Effect<void> => Effect.suspend(() => {
    const stationId = action.stationId;
    return Match.value(action.command).pipe(
      Match.tag("Select", command => Effect.gen(function* () { if (activeStation(yield* Ref.get(state), stationId)) yield* select(command.threadId); })),
      Match.tag("Draft", command => Effect.gen(function* () {
        if (!activeStation(yield* Ref.get(state), stationId)) return;
        yield* update(current => { const drafts = new Map(current.drafts); if (command.text) drafts.set(command.threadId, command.text); else drafts.delete(command.threadId);
          return { ...current, drafts, view: { ...current.view, ...(current.view.threadId === command.threadId ? { draft: command.text } : {}) } }; });
      })),
      Match.tag("AttachmentError", command => Effect.gen(function* () {
        if (!activeStation(yield* Ref.get(state), stationId)) return;
        yield* update(current => ({ ...current, view: { ...current.view, ...(command.threadId === null ? { error: command.message } : { threadError: { threadId: command.threadId, message: command.message } }) } }));
      })),
      Match.tag("Create", () => create(stationId)),
      Match.tag("Configure", command => accepted.join(Effect.gen(function* () {
        const key = chatResourceKey("configure"); if (!(yield* admit(stationId, key))) return;
        yield* update(current => ({ ...current, view: { ...current.view, configuring: true } }));
        const reply = yield* respond(api.configure(stationId, command.patch)); yield* release(key);
        if (activeStation(yield* Ref.get(state), stationId)) yield* update(current => ({ ...current, view: { ...current.view, configuring: false,
          ...(reply._tag === "Success" ? { config: current.view.config ? { ...current.view.config, ...command.patch } : null } : { error: reply.message }) } }));
        yield* finishReceipt("configure", stationId, null, null, reply);
      }))),
      Match.tag("Stop", command => accepted.join(Effect.gen(function* () {
        const { threadId } = command, key = chatResourceKey("stop", threadId); if (!(yield* admit(stationId, key))) return;
        yield* update(current => ({ ...current, view: { ...current.view, stopping: current.view.threadId === threadId || current.view.stopping } }));
        const reply = yield* respond(api.stop(stationId, threadId)); yield* release(key);
        if (activeStation(yield* Ref.get(state), stationId)) {
          yield* update(current => ({ ...current, view: { ...current.view, stopping: current.view.threadId !== null && current.pending.has(chatResourceKey("stop", current.view.threadId)),
            ...(reply._tag === "Failure" ? { threadError: { threadId, message: reply.message } } : {}) } }));
          if (reply._tag === "Success" && (yield* Ref.get(state)).view.threadId === threadId) yield* kick;
        }
        yield* finishReceipt("stop", stationId, threadId, null, reply);
      }))),
      Match.tag("Approval", command => accepted.join(Effect.gen(function* () {
        const { threadId, approvalId, approve } = command, key = chatResourceKey("approval", threadId, approvalId); if (!(yield* admit(stationId, key))) return;
        yield* update(current => ({ ...current, view: { ...current.view, approving: [...current.view.approving, key] } }));
        const reply = yield* respond(api.approval(stationId, approvalId, approve)); yield* release(key);
        if (activeStation(yield* Ref.get(state), stationId)) {
          yield* update(current => ({ ...current, view: { ...current.view, approving: current.view.approving.filter(id => id !== key),
            ...(reply._tag === "Failure" ? { threadError: { threadId, message: reply.message } } : {}),
            ...(reply._tag === "Success" && current.view.threadId === threadId ? { turnActive: reply.value.response.turnActive ?? true } : {}) } }));
          if (reply._tag === "Success" && (yield* Ref.get(state)).view.threadId === threadId) yield* kick;
        }
        yield* finishReceipt("approval", stationId, threadId, approvalId, reply);
      }))),
      Match.tag("Proposal", command => accepted.join(Effect.gen(function* () {
        const { threadId, proposalId, action } = command, key = chatResourceKey("proposal", threadId, proposalId); if (!(yield* admit(stationId, key))) return;
        yield* update(current => ({ ...current, view: { ...current.view, resolving: [...current.view.resolving, key] } }));
        const reply = yield* respond(api.proposal(stationId, threadId, proposalId, action)); yield* release(key);
        if (activeStation(yield* Ref.get(state), stationId)) {
          let changed = false;
          yield* update(current => {
            const merged = new Set(current.merged), identity = chatResourceKey(threadId, proposalId);
            if (reply._tag === "Success" && reply.value.outcome === "merged" && !merged.has(identity)) { merged.add(identity); changed = true; }
            return { ...current, merged, view: { ...current.view, resolving: current.view.resolving.filter(id => id !== key),
              ...(reply._tag === "Failure" ? { threadError: { threadId, message: reply.message } } : {}) } };
          });
          if (changed) yield* invalidate;
          if (reply._tag === "Success" && (yield* Ref.get(state)).view.threadId === threadId) yield* kick;
        }
        yield* finishReceipt("proposal", stationId, threadId, proposalId, reply);
      }))),
      Match.exhaustive
    );
  });
  return { read, snapshot, settlements: Ref.get(receipts), receive, dispatch, send };
};
